'use client';

import { useEffect, useState } from 'react';
import { Moon, Sun } from 'lucide-react';

type Theme = 'light' | 'dark';

export default function ThemeToggle() {
  // Start with null to avoid hydration mismatch
  const [theme, setTheme] = useState<Theme | null>(null);
  const [mounted, setMounted] = useState(false);

  // Hydration fix: Only render after mount
  useEffect(() => {
    setMounted(true);
    
    // Check if user has a saved preference
    const stored = localStorage.getItem('theme') as Theme | null;
    
    if (stored) {
      // User has made a choice before, use it
      setTheme(stored);
    } else {
      // Descout is dark-first: default to dark unless user explicitly chose light
      setTheme('dark');
    }
  }, []);

  // Apply theme when it changes
  useEffect(() => {
    if (!theme || !mounted) return;

    const root = document.documentElement;
    
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme, mounted]);

  // Don't render until mounted (prevents hydration mismatch)
  if (!mounted || !theme) {
    return (
      <div className="w-10 h-10" aria-hidden="true" />
    );
  }

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    // Save user's explicit choice to localStorage
    localStorage.setItem('theme', newTheme);
  };

  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-md hover:bg-surface2 dark:hover:bg-surface2 transition-colors min-w-[44px] min-h-[44px] inline-flex items-center justify-center border border-transparent hover:border-divider"
      aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
      title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
    >
      {theme === 'dark' ? (
        <Moon size={18} className="text-slate-light" />
      ) : (
        <Sun size={18} className="text-scout-gold" />
      )}
    </button>
  );
}

